import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { reportEvent } from '@/app/utils';
import {
  notificationsGetUnreadCount,
  notificationsInteractionList,
  notificationsListSysNotifications,
  notificationsReadSysNotifications,
} from '../apis';
import type { NotificationItem, NotificationTabType } from '../types';

export const NOTIFICATION_PAGE_SIZE = 10;

export const useNotifications = () => {
  const activeTab = useRef<NotificationTabType>('interaction');
  const pageToken = useRef<string>('');

  const { userInfo, subscription } = useStore((state) => ({
    userInfo: state.userInfo,
    subscription: state.subscription,
  }));

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [openSysNotificationId, setOpenSysNotificationId] = useState<string>('');
  const [system_unread_count, setSystemUnreadCount] = useState<number>(0);
  const [interaction_unread_count, setInteractionUnreadCount] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);

  // 重置分页状态
  const resetPagination = useCallback(() => {
    setNotifications([]);
    pageToken.current = '';
    setHasMore(true);
  }, []);

  // 切换标签页
  const changeActiveTab = useCallback((tab: NotificationTabType) => {
    if (activeTab.current === tab) return;
    cancelGetSystemNotification();
    cancelGetInteractionNotification();
    activeTab.current = tab;
    resetPagination();
    // 立即获取新标签页的数据
    if (tab === 'system') {
      getSystemNotification();
    } else {
      getInteractionNotification();
    }
    reportEvent('notification_list_view', {
      type: tab === 'system' ? 'platform_messages' : 'interactive_massages',
    });
  }, []);

  // 初始化通知数据
  const initNotifications = () => {
    if (system_unread_count > 0 || interaction_unread_count > 0) {
      activeTab.current = system_unread_count > 0 && interaction_unread_count === 0 ? 'system' : 'interaction';
    }
    resetPagination();
    if (activeTab.current === 'system') {
      getSystemNotification();
    } else {
      getInteractionNotification();
    }
    reportEvent('notification_list_view', {
      type: activeTab.current === 'system' ? 'platform_messages' : 'interactive_massages',
    });
  };

  useEffect(() => {
    if (!userInfo) return;
    getUnreadCountAsync().then((res) => {
      const { system_unread_count = 0, interaction_unread_count = 0 } = res.data ?? {};
      activeTab.current = system_unread_count > 0 && interaction_unread_count === 0 ? 'system' : 'interaction';
    });
  }, [userInfo]);

  // 获取未读数量
  const { run: getUnreadCount, runAsync: getUnreadCountAsync } = useFetch(notificationsGetUnreadCount, {
    refreshDeps: [subscription],
    manual: true,
    onSuccess: ({ data }) => {
      const { system_unread_count = 0, interaction_unread_count = 0 } = data ?? {};
      setSystemUnreadCount(system_unread_count);
      setInteractionUnreadCount(interaction_unread_count);
    },
  });

  // 获取系统平台通知
  const { run: getSystemNotification, cancel: cancelGetSystemNotification } = useFetch(
    () =>
      notificationsListSysNotifications({
        query: {
          'pager.page_token': pageToken.current,
          'pager.pagesz': NOTIFICATION_PAGE_SIZE,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          toast({
            type: 'error',
            message: error.message,
          });
          return;
        }

        const newNotifications: NotificationItem[] = (data?.notifications ?? []).map((item) => ({
          type: 'system' as const,
          system: item,
        }));

        setNotifications((prev) => [...prev, ...newNotifications]);
        pageToken.current = data?.next_page_token ?? '';
        setHasMore(!!data?.next_page_token);
      },
    },
  );

  // 获取互动通知
  const { run: getInteractionNotification, cancel: cancelGetInteractionNotification } = useFetch(
    () =>
      notificationsInteractionList({
        query: {
          'pager.page_token': pageToken.current,
          'pager.pagesz': NOTIFICATION_PAGE_SIZE,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          toast({
            type: 'error',
            message: error.message,
          });
          return;
        }

        const newNotifications: NotificationItem[] = (data?.notifications ?? []).map((item) => ({
          type: 'interaction' as const,
          interaction: item,
        }));

        setNotifications((prev) => [...prev, ...newNotifications]);
        pageToken.current = data?.next_page_token ?? '';
        setHasMore(!!data?.next_page_token);
      },
    },
  );

  // 获取更多通知（分页加载）
  const getNotifications = useCallback(() => {
    if (activeTab.current === 'system') {
      getSystemNotification();
    } else {
      getInteractionNotification();
    }
  }, [getSystemNotification, getInteractionNotification]);

  // 更新通知状态的辅助函数
  const updateNotificationStatus = useCallback((notificationId: string) => {
    setNotifications((prev) => {
      return prev.map((notificationItem) => {
        const shouldUpdate =
          notificationId === 'all' ||
          (notificationItem.type === 'system' && notificationItem.system?.notification?.id === notificationId) ||
          (notificationItem.type === 'interaction' && notificationItem.interaction?.id === notificationId);

        if (!shouldUpdate) return notificationItem;

        if (notificationItem.type === 'system' && notificationItem.system) {
          return {
            ...notificationItem,
            system: {
              ...notificationItem.system,
              is_read: true,
            },
          };
        }

        if (notificationItem.type === 'interaction' && notificationItem.interaction) {
          return {
            ...notificationItem,
            interaction: {
              ...notificationItem.interaction,
              status: 'read' as const,
            },
          };
        }

        return notificationItem;
      });
    });
  }, []);

  // 标记已读
  const { run: handleReadSysNotification } = useFetch(
    (notificationId: string) =>
      notificationsReadSysNotifications({
        body: {
          type: activeTab.current,
          ...(notificationId === 'all' ? { all_read: true } : { notification_id: notificationId }),
        },
      }),
    {
      manual: true,
      onSuccess: ({ error }, [notificationId]: [string]) => {
        if (error) {
          toast({
            type: 'error',
            message: error.message,
          });
          return;
        }

        // 更新未读数量
        getUnreadCount();

        // 更新本地状态
        updateNotificationStatus(notificationId);
      },
    },
  );

  // 标记全部已读
  const handleMarkAllAsRead = useCallback(() => {
    handleReadSysNotification('all');
  }, [handleReadSysNotification]);

  // 点击通知
  const handleClickSysNotification = useCallback(
    (id: string) => {
      if (!id) return;
      handleReadSysNotification(id);
    },
    [handleReadSysNotification],
  );

  // 关闭详情弹窗
  const handleCloseDetail = useCallback(() => {
    setOpenSysNotificationId('');
  }, []);

  return {
    activeTab: activeTab.current,
    notifications,
    openSysNotificationId,
    unreadCount: system_unread_count + interaction_unread_count,
    hasMore,

    // 操作函数
    changeActiveTab,
    initNotifications,
    getNotifications,
    handleMarkAllAsRead,
    handleClickSysNotification,
    handleCloseDetail,
    setOpenSysNotificationId,
  };
};
